import { User } from '@vsolv/core/users/domain';

/** Which tenant rule asked for the approval. */
export enum Type {
  /** The authorization's net payable is over the tenant's limit. */
  NET_PAYABLE_OVER = 'NET_PAYABLE_OVER',
  /** A split pays the customer part of the money. */
  CUSTOMER_SHARE = 'CUSTOMER_SHARE',
}

/** What the override holds back. */
export enum TargetType {
  JOB = 'JOB',
  CHARGE = 'CHARGE',
  AUTHORIZATION = 'AUTHORIZATION',
  ALLOCATION = 'ALLOCATION',
}

export enum State {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  /** An approval taken back. */
  REVERSED = 'REVERSED',
}

/** Every state but APPROVED holds its target back. */
export const BLOCKING_STATES = [State.PENDING, State.REJECTED, State.REVERSED];

export enum Action {
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
  REVERSE = 'REVERSE',
}

/** A manager's approval that a rule asked for before money moves. Only its state and decision change. */
export interface Model {
  id: string;

  claimId: string;

  type: Type;
  /** The rule's limit when it triggered, in cents; null for rules without one. */
  threshold: number | null;
  /** The eligibility result behind it; empty until eligibility overrides exist. */
  eligibilityId: string | null;

  targetType: TargetType;
  targetId: string;

  /** In cents. */
  requestedAmount: number;
  /** Set on approval. */
  approvedAmount: number | null;
  currency: string;

  state: State;

  /** The latest decision's note. */
  reason: string | null;
  decidedById: string | null;
  decidedBy?: User.Model | null;
  decided: Date | null;

  created: Date;
  createdById: string | null;
  createdBy?: User.Model | null;
}

export interface ListOverridesResponse {
  /** Newest first. */
  items: Model[];
}

/** How to move on once a manager rejects or reverses the approval; null while it waits or is approved. */
export function nextStep(override: Pick<Model, 'state' | 'targetType'>) {
  if (override.state !== State.REJECTED && override.state !== State.REVERSED) return null;
  // A share is fixed by a new split; anything else needs the claim rechecked.
  return override.targetType === TargetType.ALLOCATION
    ? 'Change the split to pay it another way.'
    : 'Reverse the authorization to recheck the claim.';
}

/** Why the override's target can't be paid, and what to do about it. */
export function heldBackMessage(override: Pick<Model, 'type' | 'threshold' | 'currency' | 'state' | 'targetType'>) {
  const rule = describe(override);
  switch (override.state) {
    case State.REJECTED:
      return `A manager rejected the approval for the ${rule}. ${nextStep(override)}`;
    case State.REVERSED:
      return `A manager reversed the approval for the ${rule}. ${nextStep(override)}`;
    default:
      return `Waiting for a manager to approve the ${rule}.`;
  }
}

/** e.g. "net payable over $5,000.00" or "customer share of the split". */
export function describe(override: Pick<Model, 'type' | 'threshold' | 'currency'>) {
  if (override.type === Type.CUSTOMER_SHARE) return 'customer share of the split';

  const limit = new Intl.NumberFormat('en-US', { style: 'currency', currency: override.currency }).format(
    (override.threshold ?? 0) / 100
  );
  return `net payable over ${limit}`;
}
